import { DateTime } from 'luxon'
import type { Reservation, SailPackage, SailRef } from '@/root/shared-types'
import { SAIL_STATUS } from '@/root/shared-consts'

/**
 * A reservation leg can be cancelled in two independent ways, each derived from a
 * distinct backend signal. These two predicates are the single source of truth for
 * both meanings across the app (ticket view, edit flow, refund statuses).
 *
 * - `isLegRefundedByCustomer` — the customer self-cancelled: the leg carries a
 *   refund offer (`sailPackage.refundOfferId`).
 * - `isLegCancelledByAdmin`   — the operator/admin cancelled the sail itself
 *   (`sailRef.status === 'CANCELLED'`).
 */
export const isLegRefundedByCustomer = (sailPackage: SailPackage | undefined): boolean =>
  Boolean(sailPackage?.refundOfferId)

export const isLegCancelledByAdmin = (sailRef: SailRef | undefined): boolean =>
  sailRef?.status === SAIL_STATUS.CANCELLED

/**
 * Round-trip aggregation of leg cancellation. The AND/OR asymmetry is intentional:
 *
 * - Customer refund is AND-ed across legs — a round trip only counts as cancelled by
 *   the customer once *every* leg is refunded; a half-refunded trip is still active.
 * - Admin cancellation is OR-ed across legs — a single cancelled sail blocks the whole
 *   trip, so *any* cancelled leg makes the trip admin-cancelled.
 */
export const isTripRefundedByCustomer = (
  thereRefunded: boolean,
  backRefunded: boolean | undefined,
  isRoundTrip: boolean
): boolean => (isRoundTrip ? thereRefunded && Boolean(backRefunded) : thereRefunded)

export const isTripCancelledByAdmin = (
  thereCancelled: boolean | undefined,
  backCancelled: boolean | undefined
): boolean => Boolean(thereCancelled) || Boolean(backCancelled)

/**
 * A leg is still travellable: neither side cancelled it and its sail has not departed yet.
 */
const isLegLive = (sailPackage: SailPackage | undefined, now: DateTime): boolean => {
  const sailRef = sailPackage?.sailRefs?.[0]
  if (!sailRef || isLegRefundedByCustomer(sailPackage) || isLegCancelledByAdmin(sailRef)) {
    return false
  }
  return DateTime.fromISO(sailRef.departureAt) > now
}

/**
 * Reservation still has something left to travel — at least one live leg. Round trips stay
 * live while any leg is upcoming, so an outbound that already departed does not dim the
 * booking; once every leg has either departed or been cancelled, nothing is travellable.
 */
export const hasLiveLeg = (
  reservation: Pick<Reservation, 'sailPackages' | 'departureAt'>,
  now: DateTime = DateTime.now()
): boolean => {
  const sailPackages = reservation.sailPackages ?? []
  if (sailPackages.length === 0) {
    return reservation.departureAt ? DateTime.fromISO(reservation.departureAt) > now : true
  }
  return sailPackages.some((sailPackage) => isLegLive(sailPackage, now))
}
